import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Upload, X } from "lucide-react";
import { createAdditionalRequestService, updateAdditionalRequestService } from "@/lib/additionalRequestServicesApi";
import { useToast } from "@/hooks/use-toast";
import { formatErrorMessage } from "@/lib/errorMessages";
import { MoneyInput } from "@/components/ui/money-input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const MAX_IMAGES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const formSchema = z.object({
  request_title: z.string().min(1, "Request title is required"),
  request_description: z.string().optional(),
  request_price: z
    .string()
    .optional()
    .refine((value) => {
      if (!value || value.trim() === "") return true;
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0;
    }, "Request price must be greater than or equal to 0"),
});

type FormData = z.infer<typeof formSchema>;

interface AdditionalRequestServiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSuccess: () => void;
}

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });

const AdditionalRequestServiceForm = ({
  open,
  onOpenChange,
  initialData,
  onSuccess,
}: AdditionalRequestServiceFormProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      request_title: "",
      request_description: "",
      request_price: "",
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        request_title: initialData.request_title || "",
        request_description: initialData.request_description || "",
        request_price:
          initialData.request_price === null || initialData.request_price === undefined
            ? ""
            : String(initialData.request_price),
      });
      setImages(Array.isArray(initialData.request_images) ? initialData.request_images.slice(0, MAX_IMAGES) : []);
      return;
    }

    form.reset({
      request_title: "",
      request_description: "",
      request_price: "",
    });
    setImages([]);
  }, [initialData, form, open]);

  const handleAddImages = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    if (images.length >= MAX_IMAGES) {
      toast({
        title: "Limit reached",
        description: `You can upload up to ${MAX_IMAGES} photos.`,
        variant: "destructive",
      });
      return;
    }

    const slots = MAX_IMAGES - images.length;
    const selected = files.slice(0, slots);

    for (const file of selected) {
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file",
          description: "Only image files are allowed.",
          variant: "destructive",
        });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "Image too large",
          description: "Each image must be up to 5MB.",
          variant: "destructive",
        });
        continue;
      }
      try {
        const encoded = await readFileAsDataUrl(file);
        setImages((prev) => {
          if (prev.length >= MAX_IMAGES) return prev;
          return [...prev, encoded].slice(0, MAX_IMAGES);
        });
      } catch {
        toast({
          title: "Error reading image",
          description: "Failed to process one of the selected images.",
          variant: "destructive",
        });
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (values: FormData) => {
    try {
      setSaving(true);
      const payload = {
        request_title: values.request_title.trim(),
        request_description: values.request_description?.trim() || null,
        request_price: values.request_price && values.request_price.trim() !== "" ? Number(values.request_price) : 0,
        request_images: images,
      };

      if (initialData?.id) {
        await updateAdditionalRequestService(initialData.id, payload);
        toast({ title: "Success", description: "Additional request service updated successfully" });
      } else {
        await createAdditionalRequestService(payload);
        toast({ title: "Success", description: "Additional request service created successfully" });
      }

      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: formatErrorMessage(error, "Failed to save additional request service"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit Additional Request Service" : "Add Additional Request Service"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="request_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Request Title *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="request_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Request Price</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value || ""}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Request Photos (max 3)</FormLabel>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={images.length >= MAX_IMAGES}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Images
                  </Button>
                  <span className="text-sm text-muted-foreground">{images.length}/{MAX_IMAGES}</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => void handleAddImages(e.target.files)}
                />
              </div>
            </div>

            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {images.map((image, index) => (
                  <div key={`${index}-${image.slice(0, 20)}`} className="relative rounded-md border overflow-hidden">
                    <img src={image} alt={`Request ${index + 1}`} className="h-24 w-full object-cover" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-1 right-1 h-6 w-6"
                      onClick={() => removeImage(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <FormField
              control={form.control}
              name="request_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Request Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[120px]" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AdditionalRequestServiceForm;
